import React, {useEffect, useState} from "react";
import { GraphQLResult } from "@aws-amplify/api";
import {API, graphqlOperation} from 'aws-amplify';
import { v4 as uuidv4 } from "uuid";
import {createErrorMessages, updateErrorMessages, deleteErrorMessages} from '../../graphql/mutations';
import {getErrorMessages} from '../../graphql/queries';
import {Modal} from '../Modal/Modal';
import {
  errorMessagesType,
  errorMessageType,
} from "./ErrorMessagesType";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import { Button, Dropdown, DropdownButton, Form as BootstrapForm } from "react-bootstrap";
import { FaTrash, FaEdit } from "react-icons/fa";
import { color } from "../../styles/colors";
import { ErrorKeys } from "../../API";
import { ColumnDescription, ColumnFormatter } from "react-bootstrap-table-next";
import "./ErrorMessages.css";

interface Column extends ColumnDescription {}

export const ErrorMessages = () => {
    const [enableModal, setEnableModal] = useState<boolean>(false);
    const [errorMessages, setErrorMessages] = useState<errorMessageType[]>([]);
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [errorKey, setErrorKey] = useState('');
    const [errorNumber, setErrorNumber] = useState<string | null>(null);
    const [errorDescription, setErrorDescription] = useState<string>('');
    const [enableDeleteModal, setEnableDeleteModal] = useState<boolean>(false);
    const [enableUpdateModal, setEnableUpdateModal] = useState<boolean>(false);
    const [errorMessageToDelete, setErrorMessageToDelete] = useState<
      Pick<errorMessageType, "id" | "ErrorNumber">
    >({
      id: "",
      ErrorNumber: 0
    });
      const [errorMessageToUpdate, setErrorMessageToUpdate] = useState<
        Omit<
          errorMessageType,
          "_deleted" | "_lastChangedAt" | "createdAt" | "updatedAt"
        >
      >({
        ErrorNumber: null,
        ErrorKey: "",
        ErrorDescription: "",
        InAppErrorMessage: {
          title: "",
          body: "",
        }
      });

    const renderDelete: ColumnFormatter<errorMessageType> = (cell, row) => {
      return (
        <div className={"icon-row"}>
          <button
            name="delete"
            className={"icon-button"}
            onClick={() => {
              setErrorMessageToDelete({
                id: row.id,
                ErrorNumber: row.ErrorNumber,
              });
              handleOpenDeleteModal();
            }}
          >
            <FaTrash color={color.red} />
          </button>
        </div>
      );
    };

    const renderUpdate: ColumnFormatter<errorMessageType> = (cell, row) => {
      return (
        <div className={"icon-row"}>
          <button
            name="update"
            className={"icon-button"}
            onClick={() => {
              setErrorMessageToUpdate({
                id: row.id,
                ErrorNumber: row.ErrorNumber,
                ErrorKey: row.ErrorKey,
                ErrorDescription: row.ErrorDescription,
                InAppErrorMessage: {
                  title: row.InAppErrorMessage.title,
                  body: row.InAppErrorMessage.body,
                }
              });
              setErrorKey(row.ErrorKey)
              handleOpenUpdateModal();
            }}
          >
            <FaEdit color={color.red} />
          </button>
        </div>
      );
    }

    const handleOpenModal = () => {
      setEnableModal(true)
    }

    const handleCloseModal = () => {
      setEnableModal(false)
    }

    const handleOpenDeleteModal = () => {
      setEnableDeleteModal(true);
    }

    const handleCloseDeleteModal = () => {
      setEnableDeleteModal(false);
    }

    const handleOpenUpdateModal = () => {
      setEnableUpdateModal(true);
    };

    const handleCloseUpdateModal = () => {
      setErrorMessageToUpdate({
        id: "",
        ErrorNumber: null,
        ErrorKey: "",
        ErrorDescription: "",
        InAppErrorMessage: {
          title: "",
          body: "",
        }
      });
      setErrorKey(Object.values(ErrorKeys)[0]);
      setEnableUpdateModal(false);
    }
   
    const handlePost = async () => {
      const id = uuidv4();
      await API.graphql(
        graphqlOperation(createErrorMessages, {
          errorMessages: {
            id,
            ErrorKey: errorKey,
            ErrorNumber: errorNumber,
            ErrorDescription: errorDescription,
            InAppErrorMessage: {
              title: title,
              body: body,
            }
          }
        })
      )
      fetchErrorMessages();
      handleCloseModal();
    }
    
    const handleDelete = async () => {
      try {
        await API.graphql(graphqlOperation(deleteErrorMessages, {
          errorMessagesId: errorMessageToDelete.id
      }));
      } catch (e) {
        console.log(e)
      }
      fetchErrorMessages();
      handleCloseDeleteModal();
    };

    const handleUpdate = async () => {
      const errorMessageDetails = {
        id: errorMessageToUpdate.id,
        ErrorKey: errorKey,
        ErrorDescription: errorDescription || errorMessageToUpdate.ErrorDescription,
        ErrorNumber: errorNumber || errorMessageToUpdate.ErrorNumber,
        InAppErrorMessage: {
            title: title || errorMessageToUpdate.InAppErrorMessage.title,
            body: body|| errorMessageToUpdate.InAppErrorMessage.body,
        }
      };
      try {
      await API.graphql(graphqlOperation(updateErrorMessages, {errorMessages: errorMessageDetails}));
      } catch(e) {
        console.log(e);
      }
      fetchErrorMessages();
      handleCloseUpdateModal();
    }

    const handleSelection = (eventKey, event) => {
      setErrorKey(event.target.text);
    }

    const renderErrorMessages = () => (
      <BootstrapForm>
        <BootstrapForm.Group controlId="BootstrapFormInAppErrorTitle">
          <BootstrapForm.Label>Title</BootstrapForm.Label>
          <BootstrapForm.Control
            size="lg"
            aria-label="Title"
            defaultValue={errorMessageToUpdate.InAppErrorMessage.title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </BootstrapForm.Group>
        <br />
        <BootstrapForm.Group controlId="BootstrapFormInAppErrorBody">
          <BootstrapForm.Label>Body</BootstrapForm.Label>
          <BootstrapForm.Control
            as="textarea"
            size="lg"
            aria-label="Body"
            defaultValue={errorMessageToUpdate.InAppErrorMessage.body}
            onChange={(e) => setBody(e.target.value)}
          />
        </BootstrapForm.Group>
        <br />
        <BootstrapForm.Group controlId="BootstrapFormErrorKey">
          <BootstrapForm.Label>Error Key</BootstrapForm.Label>
          <DropdownButton
            id="dropdown-basic-button"
            title={errorKey}
            variant="danger"
            size="sm"
            onSelect={handleSelection}
          >
            {Object.values(ErrorKeys).map((errorkey) => (
              <Dropdown.Item eventKey={errorkey}>
                {errorkey}
              </Dropdown.Item>
            ))}
          </DropdownButton>
        </BootstrapForm.Group>
        <br />
        <BootstrapForm.Group controlId="BootstrapFormErrorNumber">
          <BootstrapForm.Label>Error Number</BootstrapForm.Label>
          <BootstrapForm.Control
            size="lg"
            aria-label="ErrorNumber"
            defaultValue={errorMessageToUpdate.ErrorNumber}
            onChange={(e) => setErrorNumber(e.target.value)}
          />
        </BootstrapForm.Group>
        <br />
        <BootstrapForm.Group controlId="BootstrapFormErrorDescription">
          <BootstrapForm.Label>Error Description</BootstrapForm.Label>
          <BootstrapForm.Control
            size="lg"
            aria-label="ErrorDescription"
            defaultValue={errorMessageToUpdate.ErrorDescription}
            onChange={(e) => setErrorDescription(e.target.value)}
          />
        </BootstrapForm.Group>
        <br />
      </BootstrapForm>
    );

    const columns: Column[] = [
      {
        dataField: "ErrorKey",
        text: "Error Key",
      },
      {
        dataField: "ErrorNumber",
        text: "Error Number",
      },
      {
        dataField: "ErrorDescription",
        text: "Error Description",
      },
      {
        dataField: "InAppErrorMessage.title",
        text: "Error Title",
      },
      {
        dataField: "InAppErrorMessage.body",
        text: "Error Body",
      },
      {
        dataField: "UpdateErrorMessage",
        text: "Update an Error Message",
        formatter: renderUpdate,
      },
      {
        dataField: "DeleteErrorMessage",
        text: "Delete an Error Message",
        formatter: renderDelete,
      },
    ];

    useEffect(() => {
        fetchErrorMessages();
    }, []);

    const fetchErrorMessages = async () => {
        try {
            const result = (await API.graphql(
              graphqlOperation(getErrorMessages)
            )) as GraphQLResult<errorMessagesType>;
            const activeResult = result?.data?.getErrorMessages ?? [];
            setErrorMessages(activeResult);
            setErrorKey(Object.values(ErrorKeys)[0]);
        } catch(e) {
            console.log(e)
        }
    }


    const options = {
      paginationSize: 4,
      pageStartIndex: 1,
      sizePerPage: 10,
      showTotal: true,
      sizePerPageList: [
        {
          text: "5",
          value: 5,
        },
        {
          text: "10",
          value: 10,
        },
        {
          text: "20",
          value: 20,
        },
        {
          text: "40",
          value: 40,
        },
        {
          text: "Show All",
          value: errorMessages.length,
        },
      ],

      // A numeric array is also available. the purpose of above example is custom the text
    };

  return (
    <div className={"container"}>
      <h2 className="title">Error Messages</h2>
      <div className={"error-button"}>
        <Button variant="danger" onClick={handleOpenModal}>
          Create a New Error Message
        </Button>
      </div>
      <div>
        <BootstrapTable
          keyField="id"
          data={errorMessages}
          columns={columns}
          pagination={paginationFactory(options)}
        />
        <Modal
          enableModal={enableModal}
          handleClose={handleCloseModal}
          handleSubmit={handlePost}
          title={"Create a New Error Message"}
          closeButtonText="Close"
          actionButtonText="Save"
        >
          {renderErrorMessages()}
        </Modal>
        <Modal
          enableModal={enableUpdateModal}
          handleClose={handleCloseUpdateModal}
          handleSubmit={handleUpdate}
          title={"Update Error Message"}
          closeButtonText="Close"
          actionButtonText="Save"
        >
          {renderErrorMessages()}
        </Modal>
        <Modal
          enableModal={enableDeleteModal}
          handleClose={handleCloseDeleteModal}
          handleSubmit={handleDelete}
          title={"Delete Currency"}
          closeButtonText="Close"
          actionButtonText="Delete"
        >
          Are you sure that you want to delete Error Number:  {errorMessageToDelete.ErrorNumber} ?
        </Modal>
      </div>
    </div>
  );
};
